import type { FC, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { reportQueryError } from "@/lib/sentry";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { trpc, trpcClient } from "@/lib/trpc";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RequireRole } from "@/components/layout/RequireRole";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import Tenders from "./pages/Tenders";
import GoQueue from "./pages/GoQueue";
import Partners from "./pages/Partners";
import Projects from "./pages/Projects";
import Billing from "./pages/Billing";
import Messages from "./pages/Messages";
import Support from "./pages/Support";
import PrivateProjects from "./pages/PrivateProjects";
import OpsNoShows from "./pages/OpsNoShows";
import OpsQuickReports from "./pages/OpsQuickReports";
import OpsCallQueue from "./pages/OpsCallQueue";
import OpsDuplicates from "./pages/OpsDuplicates";
import OpsInactiveArtisans from "./pages/OpsInactiveArtisans";
import OpsTags from "./pages/OpsTags";
import OpsBulkGift from "./pages/OpsBulkGift";
import SalesPipeline from "./pages/SalesPipeline";
import SalesLeads from "./pages/SalesLeads";
import SalesLeadDetail from "./pages/SalesLeadDetail";
import SalesPerformance from "./pages/SalesPerformance";
import SalesForecast from "./pages/SalesForecast";
import SalesActivities from "./pages/SalesActivities";
import OnboardingPipeline from "./pages/OnboardingPipeline";
import ArtisansModeration from "./pages/ArtisansModeration";
import ArtisansAnnuaire from "./pages/ArtisansAnnuaire";
import ArtisanDetail from "./pages/ArtisanDetail";
import TravauxBTPReferrals from "./pages/TravauxBTPReferrals";
import TravauxBTPDashboard from "./pages/TravauxBTPDashboard";
import ParticulierDetail from "./pages/ParticulierDetail";
import CompanyDetail from "./pages/CompanyDetail";
import ChurnRisk from "./pages/ChurnRisk";
import LpLeads from "./pages/LpLeads";
import Disputes from "./pages/Disputes";
import DisputeDetail from "./pages/DisputeDetail";
import ConversionPipeline from "./pages/ConversionPipeline";
import Transactions from "./pages/Transactions";
import ReviewsModeration from "./pages/ReviewsModeration";
import SLALeads from "./pages/SLALeads";
import Subscriptions from "./pages/Subscriptions";
import TrialAlerts from "./pages/TrialAlerts";
import MessageModeration from "./pages/MessageModeration";
import Search from "./pages/Search";
import AuditLog from "./pages/AuditLog";
import BroadcastPage from "./pages/BroadcastPage";
import MarketplaceStats from "./pages/MarketplaceStats";
import AcquisitionFunnel from "./pages/AcquisitionFunnel";
import Refunds from "./pages/Refunds";
import FraudAlerts from "./pages/FraudAlerts";
import MarketplaceGeo from "./pages/MarketplaceGeo";
import ParticulierQuality from "./pages/ParticulierQuality";
import DocumentsExpiration from "./pages/DocumentsExpiration";
import PlansSaas from "./pages/PlansSaas";
import { NotificationListener } from "./components/NotificationListener";
import { UpdateBanner } from "./components/UpdateBanner";

const queryClient = new QueryClient({
  // Central Sentry capture for every query/mutation failure (skips expected
  // control-flow HTTP errors). Replaces the need to instrument each catch.
  queryCache: new QueryCache({
    onError: (error, query) => reportQueryError(error, { queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportQueryError(error, { mutationKey: mutation.options.mutationKey }),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DASHBOARD">
              <Dashboard />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* /users removed — staff management is in Admin app */}
      <Route path="/users" element={<Navigate to="/companies" replace />} />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="COMPANIES">
              <Companies />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="COMPANIES">
              <CompanyDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenders"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TENDERS">
              <Tenders />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/go-queue"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="GO_QUEUE">
              <GoQueue />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PARTNERS">
              <Partners />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PROJECTS">
              <Projects />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="BILLING">
              <Billing />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Categories, Audit migrated to Admin app */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SUPPORT">
              <Support />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MESSAGES">
              <Messages />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* ApiKeys, Pricing, SystemSettings, SystemAnalytics migrated to Admin app */}
      <Route
        path="/private-projects"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <PrivateProjects />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-no-shows"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsNoShows />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-quick-reports"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsQuickReports />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-call-queue"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsCallQueue />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-duplicates"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsDuplicates />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-inactive"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsInactiveArtisans />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-tags"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsTags />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-bulk-gift"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsBulkGift />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Sales routes */}
      <Route
        path="/sales/pipeline"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_PIPELINE">
              <SalesPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_LEADS">
              <SalesLeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/leads/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_LEADS">
              <SalesLeadDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/performance"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_PERFORMANCE">
              <SalesPerformance />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/forecast"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_FORECAST">
              <SalesForecast />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/activities"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_ACTIVITIES">
              <SalesActivities />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ONBOARDING">
              <OnboardingPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversion-pipeline"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONVERSION_PIPELINE">
              <ConversionPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRAVAUXBTP_DASHBOARD">
              <TravauxBTPDashboard />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisansModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans/all"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisansAnnuaire />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisanDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/reviews"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="REVIEWS_MODERATION">
              <ReviewsModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/documents"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DOCUMENTS_EXPIRATION">
              <DocumentsExpiration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/referrals"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRAVAUXBTP_REFERRALS">
              <TravauxBTPReferrals />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/particuliers/:projectId"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <ParticulierDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* FeatureFlags migrated to Admin app */}
      <Route
        path="/churn-risk"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CHURN_RISK">
              <ChurnRisk />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lp-leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="LP_LEADS">
              <LpLeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRANSACTIONS">
              <Transactions />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sla-leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SLA_LEADS">
              <SLALeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SUBSCRIPTIONS">
              <Subscriptions />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trial-alerts"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRIAL_ALERTS">
              <TrialAlerts />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messagerie-moderation"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MESSAGE_MODERATION">
              <MessageModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DISPUTES">
              <Disputes />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DISPUTES">
              <DisputeDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/refunds"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="REFUNDS">
              <Refunds />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace-stats"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MARKETPLACE_STATS">
              <MarketplaceStats />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace-geo"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MARKETPLACE_GEO">
              <MarketplaceGeo />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-funnel"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_FUNNEL">
              <AcquisitionFunnel />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fraud-alerts"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="FRAUD_ALERTS">
              <FraudAlerts />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/broadcast"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="BROADCAST">
              <BroadcastPage />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="AUDIT_LOG">
              <AuditLog />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/particulier-quality"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PARTICULIER_QUALITY">
              <ParticulierQuality />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans-saas"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PLANS_SAAS">
              <PlansSaas />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Conflit de versions @types/react (19.x exige `children` sur ReactPortal) rend
// le type de `trpc.Provider` (tRPC v10) invalide en JSX. Cast local, sans effet
// runtime, vers un composant typé.
const TrpcProvider = trpc.Provider as unknown as FC<{
  client: typeof trpcClient;
  queryClient: typeof queryClient;
  children: ReactNode;
}>;

const App = () => (
  <TrpcProvider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <UpdateBanner />
            <NotificationListener />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </TrpcProvider>
);

export default App;

